import { useEffect, useState } from "react";
import axiosInstance from "config/axiosInstance";
import { Select, MenuItem } from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import Card from "@mui/material/Card";
import { formatDatePeru, formatDateTimePeru } from "utils/datePeru";

export default function SpecialDashboard() {
  const { showAlert, AlertBanner } = useAppAlert();
  const [depositos, setDepositos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ count: 0, total: 0 });
  const [sucursales, setSucursales] = useState([]);
  const [showSucursales, setShowSucursales] = useState(false);

  const formatCurrency = (amount) => {
    const num = Number(amount || 0);
    return num.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatBancoName = (dominio) => {
    switch (dominio) {
      case "com.bcp.innovacxion.yapeapp":
        return "Yape";
      case "pe.com.interbank.mobilebanking":
        return "Plin Interbank";
      case "com.bbva.nxt_peru":
        return "Plin BBVA";
      default:
        return dominio || "-";
    }
  };

  const fetchToday = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/dashboard/hoy");
      if (res.data.success) {
        const deps = res.data.depositos || [];
        setDepositos(deps);
        
        const totalCount = deps.length;
        const totalSum = deps.reduce((acc, cur) => acc + Number(cur.monto || 0), 0);
        setSummary({ count: totalCount, total: totalSum });
      } else {
        showAlert(res.data.message || "Error al obtener depósitos del día", "error");
      }
    } catch (err) {
      showAlert("Error al obtener depósitos del día", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchSucursales = async () => {
    try {
      const res = await axiosInstance.get("/sucursales");
      if (res.data?.success) {
        const sucs = res.data.sucursales || [];
        setSucursales(sucs);
        setShowSucursales(sucs.length > 0);
      }
    } catch (err) {
      // If error (e.g., personal user), don't show sucursales
    }
  };

  useEffect(() => {
    fetchToday();
    fetchSucursales();
  }, []);

  const handleAsignarSucursal = async (depositoId, sucursalId) => {
    try {
      const sucursalNombre = sucursalId 
        ? sucursales.find(s => s.id === parseInt(sucursalId))?.nombre_sucursal || "sucursal"
        : "Sin asignar";

      await axiosInstance.post(
        "/sucursales/assign-deposit",
        { depositoId: depositoId, sucursalId: sucursalId || null }
      );
      
      // Update local state with nombre_sucursal
      setDepositos((prev) =>
        prev.map((d) => (d.id === depositoId ? { ...d, tienda_id: sucursalId || null, nombre_sucursal: sucursalNombre === "Sin asignar" ? null : sucursalNombre } : d))
      );

      const mensaje = sucursalId
        ? `Depósito asignado a: ${sucursalNombre}`
        : "Depósito sin asignar";
      showAlert(mensaje, "success");
    } catch (err) {
      showAlert(err.response?.data?.message || "Error al asignar sucursal", "error");
    }
  };

  const formatearFecha = (fecha) => formatDateTimePeru(fecha);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <AlertBanner />
        <Card sx={{ borderRadius: "16px" }}>
          <VuiBox p={3}>
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <VuiTypography variant="h4" color="white" fontWeight="bold">
                📋 Registros del día
              </VuiTypography>
              <VuiTypography variant="button" color="text">
                (Se reinicia al siguiente día)
              </VuiTypography>
            </VuiBox>

            {/* Summary */}
            <VuiBox
              mb={3}
              p={2}
              sx={{
                backgroundColor: "rgba(106, 13, 173, 0.2)",
                borderRadius: "12px",
                border: "1px solid rgba(106, 13, 173, 0.4)",
              }}
            >
              <VuiBox display="flex" justifyContent="space-between" alignItems="center">
                <VuiTypography variant="button" color="white" fontWeight="medium">
                  Total depósitos: <strong>{summary.count ?? 0}</strong>
                </VuiTypography>
                <VuiTypography variant="button" color="success" fontWeight="bold">
                  Total del día ({formatDatePeru(new Date())}): S/ {formatCurrency(summary.total)}
                </VuiTypography>
              </VuiBox>
            </VuiBox>

            {/* Table */}
            {loading ? (
              <VuiTypography color="text" textAlign="center">Cargando...</VuiTypography>
            ) : depositos.length > 0 ? (
              <VuiBox
                sx={{
                  overflowX: "auto",
                  maxHeight: "600px",
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "10px",
                    "&:hover": {
                      background: "rgba(255, 255, 255, 0.3)",
                    },
                  },
                }}
              >
                {/* Header */}
                <VuiBox
                  display="flex"
                  sx={{
                    backgroundColor: "rgba(106, 13, 173, 0.2)",
                    borderRadius: "10px",
                    p: 2,
                    mb: 1,
                    border: "1px solid rgba(106, 13, 173, 0.4)",
                    minWidth: showSucursales ? "1100px" : "950px",
                  }}
                >
                  <VuiTypography
                    color="white"
                    fontWeight="bold"
                    fontSize="0.9rem"
                    sx={{ width: "5%", textAlign: "center" }}
                  >
                    #
                  </VuiTypography>
                  <VuiTypography
                    color="white"
                    fontWeight="bold"
                    fontSize="0.9rem"
                    sx={{ width: showSucursales ? "20%" : "25%" }}
                  >
                    Fecha
                  </VuiTypography>
                  <VuiTypography
                    color="white"
                    fontWeight="bold"
                    fontSize="0.9rem"
                    sx={{ width: showSucursales ? "27%" : "32%" }}
                  >
                    Nombre
                  </VuiTypography>
                  <VuiTypography
                    color="white"
                    fontWeight="bold"
                    fontSize="0.9rem"
                    sx={{ width: showSucursales ? "13%" : "18%" }}
                  >
                    Banco
                  </VuiTypography>
                  {showSucursales && (
                    <VuiTypography
                      color="white"
                      fontWeight="bold"
                      fontSize="0.9rem"
                      sx={{ width: "20%", textAlign: "center" }}
                    >
                      Sucursal
                    </VuiTypography>
                  )}
                  <VuiTypography
                    color="white"
                    fontWeight="bold"
                    fontSize="0.9rem"
                    sx={{ width: "15%", textAlign: "right" }}
                  >
                    Monto
                  </VuiTypography>
                </VuiBox>

                {/* Rows */}
                {depositos.map((deposito, index) => (
                  <VuiBox
                    key={deposito.id}
                    display="flex"
                    alignItems="center"
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.03)",
                      borderRadius: "8px",
                      p: 2,
                      mb: 1,
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                      minWidth: showSucursales ? "1100px" : "950px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.06)",
                        borderColor: "rgba(0, 200, 83, 0.3)",
                        transform: "translateX(5px)",
                      },
                    }}
                  >
                    <VuiTypography
                      color="text"
                      fontSize="0.9rem"
                      sx={{ width: "5%", textAlign: "center" }}
                    >
                      {index + 1}
                    </VuiTypography>
                    <VuiTypography color="text" fontSize="0.85rem" sx={{ width: showSucursales ? "20%" : "25%" }}>
                      {formatearFecha(deposito.fecha)}
                    </VuiTypography>
                    <VuiTypography color="white" fontSize="0.85rem" sx={{ width: showSucursales ? "27%" : "32%" }}>
                      {deposito.nombre || "-"}
                    </VuiTypography>
                    <VuiTypography color="white" fontSize="0.85rem" sx={{ width: showSucursales ? "13%" : "18%" }}>
                      {formatBancoName(deposito.dominio)}
                    </VuiTypography>
                    {showSucursales && (
                      <VuiBox sx={{ width: "20%", px: 1 }}>
                        <Select
                          value={deposito.tienda_id ? String(deposito.tienda_id) : ""}
                          onChange={(e) => handleAsignarSucursal(deposito.id, e.target.value)}
                          displayEmpty
                          size="small"
                          renderValue={(selected) => {
                            if (!selected) {
                              return <em style={{ color: "rgba(255, 255, 255, 0.5)" }}>Sin asignar</em>;
                            }
                            return deposito.nombre_sucursal || "Cargando...";
                          }}
                          sx={{
                            width: "100%",
                            color: deposito.tienda_id ? "#00c853" : "white",
                            backgroundColor: deposito.tienda_id 
                              ? "rgba(0, 200, 83, 0.15)" 
                              : "rgba(255, 255, 255, 0.08)",
                            borderRadius: "8px",
                            fontSize: "0.8rem",
                            fontWeight: deposito.tienda_id ? "bold" : "normal",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: deposito.tienda_id 
                                ? "rgba(0, 200, 83, 0.5)" 
                                : "rgba(255, 255, 255, 0.2)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 200, 83, 0.7)",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#00c853",
                            },
                            "& .MuiSelect-icon": {
                              color: deposito.tienda_id 
                                ? "#00c853" 
                                : "rgba(255, 255, 255, 0.7)",
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: "#1a1d21",
                                border: "1px solid rgba(255, 255, 255, 0.1)",
                                "& .MuiMenuItem-root": {
                                  color: "white",
                                  fontSize: "0.85rem",
                                  "&:hover": {
                                    backgroundColor: "rgba(0, 200, 83, 0.1)",
                                  },
                                  "&.Mui-selected": {
                                    backgroundColor: "rgba(0, 200, 83, 0.2)",
                                    "&:hover": {
                                      backgroundColor: "rgba(0, 200, 83, 0.3)",
                                    },
                                  },
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Sin asignar</em>
                          </MenuItem>
                          {sucursales.map((s) => (
                            <MenuItem key={s.id} value={String(s.id)}>
                              {s.nombre_sucursal}
                            </MenuItem>
                          ))}
                        </Select>
                      </VuiBox>
                    )}
                    <VuiTypography
                      color="success"
                      fontSize="1rem"
                      fontWeight="bold"
                      sx={{ width: "15%", textAlign: "right" }}
                    >
                      S/. {formatCurrency(deposito.monto)}
                    </VuiTypography>
                  </VuiBox>
                ))}
              </VuiBox>
            ) : (
              <VuiBox
                textAlign="center"
                py={4}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  borderRadius: "12px",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                }}
              >
                <VuiTypography color="text" fontSize="1rem">
                  No hay depósitos registrados hoy.
                </VuiTypography>
              </VuiBox>
            )}
          </VuiBox>
        </Card>
      </VuiBox>
    </DashboardLayout>
  );
}

